/* eslint-disable filename-rules/match */
import { useCallback, useEffect, useMemo, useState } from 'react'

import styled from '@emotion/styled'
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { PHASE_PRODUCTION_BUILD } from 'next/constants'
import Head from 'next/head'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'

import { spacing, Text } from '@cash-design-system/react'
import logger from '@cash-web/shared/util-logger'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { ContactUs, Search, StatusBadge } from '@cash-web/support/feature-main'
import { ParsedArticleContent, qrHashes, QrThemes, SupportSuperDuperMainTitle } from '@cash-web/support/ui-components'
import { SupportAction, useAnalyticsContext } from '@cash-web/support/utils'
import { defaultRegion, supportedRegions } from '@cash-web/support/utils-region-config'
import { CustomerSupportAccessViewNodes, WebInteractClick, WebViewViewPage } from '@squareup/customer-data-events-web'

import getRecommendedArticles from '../../../data/recommendedArticles'
import i18nextConfig, { fallbackLng as defaultLocale } from '../../../next-i18next.config'
import { getArticleFromSupportArticles } from '../../../src/api'
import ArticleList from '../../../src/components/ArticleList'
import HelpContent from '../../../src/components/helpContent'
import RegionalisedLink from '../../../src/components/RegionalisedLink'
import useCanonicalURL from '../../../src/hooks/useCanonicalUrl'
import useSearchQuery from '../../../src/hooks/useSearchQuery'
import type { Article, ArticleListing, ArticleListingWithToken } from '../../../types'
import { isSupportedLocale } from '../../../utils/LocaleDetection'

const StyledArticle = styled.article`
  width: 100%;
  max-width: 48rem;
  display: flex;
  flex-direction: column;
  gap: ${spacing.l};
  margin: 0 auto;
`

const contactArticleToken = process.env['NEXT_PUBLIC_CONTACT_ARTICLE_ID']
const deepLinkPath = process.env['NEXT_PUBLIC_DEEPLINK_PATH_PREFIX']
const frontendServiceUrl = process.env['NEXT_PUBLIC_FRONTEND_SERVICE_URL']
const qrPath = process.env['NEXT_PUBLIC_QR_PATH_PREFIX']

const cacheTime =
  process.env['ARTICLE_CACHE_TIME'] && Number.isInteger(Number(process.env['ARTICLE_CACHE_TIME']))
    ? parseInt(process.env['ARTICLE_CACHE_TIME'])
    : 3600

interface ArticleProps {
  article: Article
  slugifiedPath: string
  childArticles?: ArticleListing[]
}

interface RegionLocaleResult {
  region: string
  locale: string
}

const ContentContainer = styled.div`
  background: ${props => props.theme.colors.background};
`

/**
 * Determines the valid region and locale based on input params.
 * @param region - The region provided in the request.
 * @param locale - The locale provided in the request.
 * @returns A valid region and locale.
 */
export const getRegionAndLocale = (region: string | undefined, locale: string): RegionLocaleResult => {
  const regionToRequest = region?.toLowerCase()
  const isRegionSupported = regionToRequest && Object.keys(supportedRegions).includes(regionToRequest)

  // If region is unsupported, default to the global fallback
  if (!isRegionSupported) {
    return {
      region: defaultRegion,
      locale: supportedRegions[defaultRegion]?.defaultLocale ?? defaultLocale,
    }
  }

  const regionSupportedLocales =
    supportedRegions[regionToRequest as keyof typeof supportedRegions]?.supportedLocales || []
  const localeToRequest = isSupportedLocale(locale, regionSupportedLocales)
    ? locale
    : (supportedRegions[regionToRequest as keyof typeof supportedRegions]?.defaultLocale ?? defaultLocale)

  return {
    region: regionToRequest,
    locale: localeToRequest,
  }
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps<{
  article: Article
  slugifiedPath: string
  childArticles?: ArticleListing[]
}> = async ({ locale = defaultLocale, params }) => {
  const articleSlug = params?.['article'] as string | undefined
  const articleToken = articleSlug?.match(/^(\d+)[a-zA-Z0-9-]*$/)?.[1]

  const paramRegion = params?.['region']?.toString().toLowerCase()

  // Validate Article Token
  if (!articleToken) {
    return { notFound: true, revalidate: cacheTime }
  }

  const { region: regionToRequest, locale: localeToRequest } = getRegionAndLocale(paramRegion, locale)

  // Fetch article and translations
  return Promise.all([
    getArticleFromSupportArticles({ token: articleToken, locale: localeToRequest, region: regionToRequest }),
    serverSideTranslations(locale, ['common'], i18nextConfig),
  ]).then(([{ article: responseArticle }, translations]) => {
    if (!responseArticle?.token) {
      const regionDefaultLocale =
        supportedRegions[regionToRequest as keyof typeof supportedRegions]?.defaultLocale ?? defaultLocale

      // Article not published in the requested locale: fall back to the region default.
      // Absolute URL on purpose — a relative destination is re-entered by the SPA router in
      // the same locale (redirect loop) and by the edge in a non-canonical shape.
      // Known limitation: query params (e.g. searchText) are dropped. getStaticProps can't
      // see the request query, and the redirect is ISR-cached per path, so one user's query
      // can't safely be baked into the shared destination.
      if (localeToRequest !== regionDefaultLocale) {
        return {
          redirect: {
            permanent: false,
            destination: `${frontendServiceUrl}/help/${regionToRequest}/${regionDefaultLocale}/${articleSlug}`,
          },
          revalidate: 30,
        }
      }

      return { notFound: true, revalidate: 30 }
    }

    // Redirect if there's a redirect_token from the CMS
    if (responseArticle.redirect_token && articleToken != responseArticle.redirect_token) {
      return {
        redirect: { permanent: false, destination: `/${regionToRequest}/${responseArticle.redirect_token}` },
        revalidate: cacheTime,
      }
    }

    const slugifiedPath = [articleToken, slugify(responseArticle.title)].filter(text => text).join('-') // The slugified URL only includes the title if it is set

    // Redirect if the title isn't slugified in the URL and it isn't the contact page, except at build time which isn't supported
    if (
      articleSlug != slugifiedPath &&
      responseArticle.token !== contactArticleToken &&
      process.env['NEXT_PHASE'] !== PHASE_PRODUCTION_BUILD
    ) {
      const regionPrefix = regionToRequest == defaultRegion ? '' : `/${regionToRequest}`
      const regionDefaultLocale =
        supportedRegions[regionToRequest as keyof typeof supportedRegions]?.defaultLocale ?? defaultLocale
      const localePrefix =
        localeToRequest.toLowerCase() == regionDefaultLocale.toLowerCase() ? '' : `/${localeToRequest}`

      return {
        redirect: { permanent: false, destination: `${regionPrefix}${localePrefix}/${slugifiedPath}` },
        revalidate: cacheTime,
      }
    }

    const article: Article = {
      title: responseArticle.title ?? '',
      content: responseArticle.body ?? '',
      token: responseArticle.token ?? '',
      metaDescription: responseArticle.meta_description,
      metaTitle: responseArticle.meta_title,
    }

    const filteredChildArticles: ArticleListing[] | undefined = responseArticle.child_articles
      ?.map(childArticle => ({
        title: childArticle.title,
        token: childArticle.token,
        content: childArticle.summary,
        slug: childArticle.slug,
      }))
      ?.filter(childArticle => !!childArticle.token) as ArticleListing[]

    return {
      props: {
        article,
        slugifiedPath,
        childArticles: filteredChildArticles ?? null,
        ...translations,
      },
      revalidate: cacheTime,
    }
  })
}

const DESKTOP_QR_SIZE = 300

const ArticlePage: NextPage<ArticleProps> = ({ article, slugifiedPath, childArticles }) => {
  const { viewToken } = useAnalyticsContext()
  const canonicalUrl = useCanonicalURL(slugifiedPath)
  const { t } = useTranslation()
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const desktopQRSize = DESKTOP_QR_SIZE
  const launchChatDeepLink = `${deepLinkPath}support/chat?page=help`
  const logCdfEvent = useLogCashWebCdfEvent()
  const searchParams = useSearchParams()
  const [query, setQuery] = useState('')

  const onHashChanged = () => {
    const hash = window.location.hash
    setOpen(hash === qrHashes['chat'])
  }

  // Prevent the contact route from having the basePath prepended to it
  useEffect(() => {
    const originalReplaceState = window.history.replaceState

    window.history.replaceState = (state, _unused, url) => {
      // If on /contact, ignoring get parameters, and requesting to replace to /help/contact
      if (router.asPath.replace(/\?.*/, '') === '/contact' && url?.toString().replace(/\?.*/, '') === '/help/contact') {
        return undefined
      }

      return originalReplaceState.apply(window.History, [state, _unused, url])
    }

    return () => {
      window.history.replaceState = originalReplaceState
    }
  }, [router])

  const displayTypes = useMemo(() => childArticles?.map(() => 'node_tree'), [childArticles])

  const nodeTokens = useMemo(
    () => childArticles?.filter(item => 'token' in item).map(item => (item as ArticleListingWithToken).token),
    [childArticles]
  )

  const pageName = useMemo(
    () => (article.token == contactArticleToken ? 'Support Contact Page' : 'Support Article Page'),
    [article.token]
  )

  const logPageAnalytics = useCallback(() => {
    logger.addBreadcrumb(SupportAction.ArticleLoaded)
    const searchText = searchParams.get('searchText')
    const totalCount = childArticles?.length || 0
    logCdfEvent(
      WebViewViewPage({
        pageName,
      })
    )
    logCdfEvent(
      CustomerSupportAccessViewNodes({
        displayTypes: displayTypes?.toString(),
        nodeTokens: nodeTokens?.toString(),
        parentToken: article.token,
        totalCount,
        trigger: 'NODE',
        viewToken: viewToken,
        searchText: searchText || '',
      })
    )
  }, [searchParams, childArticles?.length, logCdfEvent, pageName, displayTypes, nodeTokens, article.token, viewToken])

  useEffect(() => {
    // check on initial page load
    if (typeof window !== 'undefined' && router.isReady) {
      logPageAnalytics()
      onHashChanged()
    }

    window?.addEventListener('hashchange', onHashChanged)

    return () => {
      window?.removeEventListener('hashchange', onHashChanged)
    }
  }, [logPageAnalytics, router.isReady])

  const logLinkClick = useCallback(
    (event: MouseEvent) => {
      const link = event.target as HTMLAnchorElement

      if (logCdfEvent && pageName) {
        logCdfEvent(
          WebInteractClick({
            pageName: pageName,
            componentName: link.href,
            componentType: link.tagName,
          })
        )
      }
    },
    [logCdfEvent, pageName]
  )

  useEffect(() => {
    // Only run client-side
    if (typeof window !== 'undefined' && article.token) {
      document.querySelectorAll<HTMLAnchorElement>('article a').forEach((link: HTMLAnchorElement) => {
        link.removeEventListener('click', logLinkClick)
        link.removeEventListener('auxclick', logLinkClick)
        link.addEventListener('click', logLinkClick, true)
        link.addEventListener('auxclick', logLinkClick, true)
      })
    }
  }, [article.token, logLinkClick])

  const closeQrModal = useCallback(() => {
    // Remove #chat from the URL
    if (router.asPath.startsWith('/contact')) {
      window?.history.replaceState(null, '', '/contact') // We don't want the basePath to be added back in on the /contact page, which router does
    } else {
      router.replace(
        {
          hash: '',
          pathname: router.pathname,
          query: { region: defaultRegion, article: slugifiedPath, ...router.query },
        },
        router.asPath.replace(new RegExp(`${qrHashes['chat']}$`), ''),
        { shallow: true }
      )
    }

    setOpen(false)
  }, [router, slugifiedPath])

  const { data: searchData, isLoading, isError } = useSearchQuery({ query, showMoreResults: true })

  return (
    <>
      <Head>
        <title>{article.metaTitle ?? article.title}</title>
        <meta name="description" content={article.metaDescription} />
        {article.token == contactArticleToken ? (
          <link rel="canonical" href={`${frontendServiceUrl}/contact`} />
        ) : (
          canonicalUrl
        )}
      </Head>
      <ContentContainer>
        <HelpContent>
          <section>
            <StatusBadge t={t} />
            {/* TODO: janec cameronandrews update fontWeight to 600 once Cash Sans font is applied to help center */}
            <SupportSuperDuperMainTitle fontWeight={500} t={t} />
            <Search
              invert
              showMoreResults
              recommendedArticles={getRecommendedArticles(t)}
              data={searchData}
              isLoading={isLoading}
              isError={isError}
              initialHash={query}
              onQueryChange={setQuery}
              Link={RegionalisedLink}
              t={t}
            />
          </section>
          <section>
            <StyledArticle>
              <Text data-testid={article.token} color="textStandard" typography="numeralSmall" as="h1">
                {article.title}
              </Text>
              <ParsedArticleContent content={article.content} />
            </StyledArticle>
          </section>
        </HelpContent>
      </ContentContainer>
      <HelpContent>
        {childArticles?.length ? (
          <section>
            <ArticleList
              title={t('Related', {
                context: 'Subheading on the help article page with a list of articles related to the current article',
              })}
              data={childArticles}
              trigger="NODE"
              analyticsType="NODE_TREE"
            />
          </section>
        ) : undefined}
        <section>
          <ContactUs
            mobileChatDeepLink={launchChatDeepLink}
            desktopQRPath={`${qrPath}launch/support/chat`}
            desktopQRSize={desktopQRSize}
            desktopQRTheme={QrThemes.LIGHT}
            open={open}
            onCloseQrModal={closeQrModal}
            cdfTrigger={{
              viewToken: viewToken,
              trigger: 'NODE',
              triggerNodeToken: article.token,
            }}
            t={t}
          />
        </section>
      </HelpContent>
    </>
  )
}

/** Converts text into a slug */
export const slugify = (text?: string) =>
  text
    ?.replaceAll(/[^a-z0-9 ]/gi, '')
    .trim()
    .replaceAll(' ', '-')
    .toLowerCase() ?? ''

export default ArticlePage
